import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { FEE_UNIT_TYPES } from 'src/constants/feesSetting';
import { useStore } from 'src/stores';
import useStyles from '../styles';

/**
 * The property's own account setup fee amount, for the AppFolio add-property wizard (LB-6148).
 *
 * The rules are the ones the fee is already edited by on the other two screens: the customer's price
 * is both the placeholder and the ceiling, an empty box means the property has set no amount of its
 * own and is charged the price, and an amount above the price is refused with the price named. A
 * null price -- flag off, or the product not on this property's subscription -- hides the field, as
 * it does everywhere else the fee appears.
 *
 * Children sit beside the amount on its row, which is where this step keeps a fee's GL account.
 */
export const AccountSetupFee = observer(_ref => {
  let children = _ref.children,
    isLoaded = _ref.isLoaded,
    price = _ref.price;
  const store = useStore();
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const feeStructureV2 = store.addNewProperty.feeStructureV2;
  const getStoredAmount = () => {
    var _feeStructureV2$getPr, _feeStructureV2$getPr2;
    return (_feeStructureV2$getPr = (_feeStructureV2$getPr2 = feeStructureV2.getPropertyFee({
      feeName: 'accountSetupFee'
    })) === null || _feeStructureV2$getPr2 === void 0 ? void 0 : _feeStructureV2$getPr2.value) !== null && _feeStructureV2$getPr !== void 0 ? _feeStructureV2$getPr : '';
  };
  const _useState = useState(getStoredAmount),
    _useState2 = _slicedToArray(_useState, 2),
    amount = _useState2[0],
    setAmount = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    overMax = _useState4[0],
    setOverMax = _useState4[1];

  // The property's fees load after mount, so re-seed from the store once they arrive.
  useEffect(() => {
    setAmount(getStoredAmount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);
  const handleChange = e => {
    const value = e.target.value;
    setOverMax(value !== '' && +value > price);
    if (value === '') {
      setAmount('');
      feeStructureV2.updateFee({
        feeName: 'accountSetupFee',
        value: null
      });
    } else if (+value <= price && /^\d+([.,]\d{0,2})?$/.test(+value)) {
      setAmount(value);
      feeStructureV2.updateFee({
        feeName: 'accountSetupFee',
        value: +value,
        feeUnitType: FEE_UNIT_TYPES.fixedAmount
      });
    }
  };
  if (price === null) return null;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('add_new_property_flow.asf')), /*#__PURE__*/React.createElement("div", {
    className: classes.xsSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, t('add_new_property_flow.asf_description', {
    price: price.toFixed(2)
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.feeSetupWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.feeSetupCharge
  }, /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    error: overMax,
    onKeyPress: event => {
      if (!/[0-9]|[.,]/.test(event.key)) {
        event.preventDefault();
      }
    },
    onChange: handleChange
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "account-setup-fee"
  }, t('add_new_property_flow.fee_value')), /*#__PURE__*/React.createElement(OutlinedInput, {
    id: "account-setup-fee",
    label: t('add_new_property_flow.fee_value'),
    type: "number",
    value: amount,
    placeholder: price.toFixed(2),
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, "$")
  }), overMax && /*#__PURE__*/React.createElement(FormHelperText, null, t('add_new_property_flow.asf_max', {
    price: price.toFixed(2)
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.feeSetupGl
  }, children)), /*#__PURE__*/React.createElement("div", {
    className: classes.xsSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, t('add_new_property_flow.asf_caption'))));
});